import axios from 'axios';
import DsoTools from '@/lib/es-bill-shared-js/misc/DsoTools';
import {
  extractDateRangesFromDateArray,
  isDateValid,
} from '@/lib/es-bill-shared-js/misc/timeTools';
import { BACKEND_BASEURL } from '@/config/config';
import { shouldIncludeBillrun } from '@/services/billrunEnergyDataExportsService';
import { getFilteredIssueResults } from '@/services/billrunEnergyDataInterpretationService';
import {
  formatDateRangesForDisplay,
  normalizeToLocalMidnight,
  passesViewFilters,
  toFilterSets,
} from '@/services/billrunFiltersService';
import { startEnergyDataRequest } from '@/services/meteringPointMembershipService';
import { billRunAnalysisKey } from '@/stores/billrunsStore';

/** Max concurrent startEnergyDataRequest calls (matches billrun analysis fetch). */
const SUBMIT_CONCURRENCY = 5;

/**
 * @typedef {Object} BuildEnergyDataRequestsInput
 * @property {Array} billruns
 * @property {Record<string, object|null>} analysisByBillRunKey
 * @property {Record<string, import('@/services/billrunEnergyDataInterpretationService').EnergyDataInterpretation>} interpretationByBillRunKey
 * @property {boolean} billingRelevantOnly
 * @property {string[]} selectedDsoEcns
 * @property {string[]} selectedCommunityEcids
 * @property {Map<string, object>} communitiesByEcid
 * @property {Array} [memberships] - optional MPM store snapshot; fetches from API when omitted or empty
 */

/**
 * @typedef {Object} BuildEnergyDataRequestsResult
 * @property {Array<{ meteringPointsData: object[], dateStart: Date, dateEnd: Date }>} requests
 * @property {Array<{ ecid: string, ecn: string, meteringPoint: string }>} unresolvedMeterPoints
 * @property {EnergyDataRequestSummary} summary
 */

/**
 * @typedef {Object} EnergyDataRequestSummaryTotals
 * @property {number} requestCount
 * @property {number} meteringPointCount
 * @property {number} communityCount
 * @property {number} dsoCount
 * @property {number} unresolvedMeterPointCount
 */

/**
 * @typedef {Object} EnergyDataRequestSummaryGroup
 * @property {string} displayLabel
 * @property {number} meteringPointCount
 * @property {number} requestCount
 * @property {string[]} dateRanges
 */

/**
 * @typedef {Object} EnergyDataRequestSummary
 * @property {EnergyDataRequestSummaryTotals} totals
 * @property {Array<EnergyDataRequestSummaryGroup & { ecid: string, ecn: string }>} byCommunity
 * @property {Array<EnergyDataRequestSummaryGroup & { ecnDso: string }>} byDso
 * @property {Array<{ ecid: string, ecn: string, meteringPoint: string }>} unresolvedMeterPoints
 */

/**
 * @typedef {Object} AggregatedMeterPoint
 * @property {string} ecid
 * @property {string} ecn
 * @property {string} meteringPoint
 * @property {Date[]} dates
 */

/**
 * Whether filtered analysis has entries in the three request-relevant categories.
 *
 * @param {import('@/services/billrunEnergyDataInterpretationService').FilteredIssueResults} filtered
 * @returns {boolean}
 */
export function hasRelevantIssueEntries(filtered) {
  if (filtered.incorrectCountOfQhResults.length > 0) {
    return true;
  }
  if (filtered.uncleanMeteringPointsDetailResults.length > 0) {
    return true;
  }
  return filtered.incorrectMeterCodesResults.some(
    (entry) => Array.isArray(entry?.meterCodesMissing) && entry.meterCodesMissing.length > 0
  );
}

/**
 * Parse an analysis issue date to local midnight; invalid or missing values return null.
 *
 * @param {unknown} dateValue
 * @returns {Date|null}
 */
function parseIssueDate(dateValue) {
  if (dateValue == null) {
    return null;
  }
  const d = normalizeToLocalMidnight(new Date(dateValue));
  return isDateValid(d) ? d : null;
}

/**
 * Collect issue dates keyed by meteringPoint from the three relevant categories.
 *
 * @param {import('@/services/billrunEnergyDataInterpretationService').FilteredIssueResults} filtered
 * @returns {Map<string, Date[]>}
 */
function collectIssueDatesByMeterPoint(filtered) {
  const byMp = new Map();

  const addDate = (meteringPoint, dateValue) => {
    if (typeof meteringPoint !== 'string' || meteringPoint.length === 0) {
      return;
    }
    const issueDate = parseIssueDate(dateValue);
    if (!issueDate) {
      return;
    }
    if (!byMp.has(meteringPoint)) {
      byMp.set(meteringPoint, []);
    }
    byMp.get(meteringPoint).push(issueDate);
  };

  for (const entry of filtered.incorrectCountOfQhResults) {
    addDate(entry?.meteringPoint, entry?.date);
  }

  for (const entry of filtered.uncleanMeteringPointsDetailResults) {
    addDate(entry?.meteringPoint, entry?.date);
  }

  for (const entry of filtered.incorrectMeterCodesResults) {
    if (
      !Array.isArray(entry?.meterCodesMissing)
      || entry.meterCodesMissing.length === 0
    ) {
      continue;
    }
    addDate(entry?.meteringPoint, entry?.date);
  }

  return byMp;
}

/**
 * Dedupe issue dates by timestamp and merge consecutive days into inclusive ranges.
 *
 * @param {Date[]} dates
 * @returns {{ dateStart: Date, dateEnd: Date }[]}
 */
function datesToDateRanges(dates) {
  if (dates.length === 0) {
    return [];
  }
  const unique = [...new Map(dates.map((d) => [d.getTime(), d])).values()];
  return extractDateRangesFromDateArray(unique).map(([dateStart, dateEnd]) => ({
    dateStart,
    dateEnd,
  }));
}

/**
 * Resolve DSO ECN from the metering-point prefix (first 8 chars).
 *
 * @param {object|null|undefined} community
 * @param {string} meteringPoint
 * @returns {string}
 */
function resolveEcnDso(community, meteringPoint) {
  const prefix = meteringPoint.substring(0, 8);
  try {
    if (!community) {
      throw new Error('community missing');
    }
    return DsoTools.detectDso(community, prefix);
  } catch {
    return prefix;
  }
}

/**
 * Merge dates from source into target aggregation bucket.
 *
 * @param {AggregatedMeterPoint} target
 * @param {Date[]} sourceDates
 */
function mergeDatesIntoBucket(target, sourceDates) {
  const dateMap = new Map(target.dates.map((d) => [d.getTime(), d]));
  for (const d of sourceDates) {
    dateMap.set(d.getTime(), d);
  }
  target.dates = [...dateMap.values()];
}

/**
 * Whether a membership's active period overlaps a fetch date range.
 * Mirrors the overlap rules in meteringPointMembershipService.selectDateRangePerMp.
 *
 * @param {object} mpm
 * @param {Date} dateStart
 * @param {Date} dateEnd
 * @returns {boolean}
 */
function _membershipOverlapsDateRange(mpm, dateStart, dateEnd) {
  const fetchStart = new Date(dateStart);
  const fetchEnd = new Date(dateEnd);

  if (mpm.membershipStatus === 'ACTIVE') {
    if (mpm.dateStart > fetchEnd) {
      return false;
    }
    return true;
  }

  if (mpm.membershipStatus === 'ENDED' || mpm.membershipStatus === 'ENDING') {
    if (mpm.dateEnd < fetchStart || mpm.dateStart > fetchEnd) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * Pick the membership that covers the fetch period; prefers ACTIVE, then latest dateStart.
 *
 * @param {object[]} candidates
 * @param {Date} dateStart
 * @param {Date} dateEnd
 * @returns {object|null}
 */
function _resolveMembershipForDateRange(candidates, dateStart, dateEnd) {
  const overlapping = candidates.filter((mpm) =>
    _membershipOverlapsDateRange(mpm, dateStart, dateEnd)
  );
  if (overlapping.length === 0) {
    return null;
  }

  overlapping.sort((a, b) => {
    const statusRank = (mpm) => (mpm.membershipStatus === 'ACTIVE' ? 0 : 1);
    const rankDiff = statusRank(a) - statusRank(b);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    return b.dateStart.getTime() - a.dateStart.getTime();
  });

  return overlapping[0];
}

/**
 * Build lookup map for membership resolution (all memberships per ecid:ecn:meteringPoint).
 *
 * @param {Array} memberships
 * @returns {Map<string, object[]>}
 */
export function buildMembershipLookup(memberships) {
  const lookup = new Map();
  for (const mpm of memberships) {
    const ecid = mpm?.ecid;
    const ecn = mpm?.ecn;
    const meteringPoint = mpm?.meteringPoint;
    if (typeof ecid !== 'string' || typeof ecn !== 'string' || typeof meteringPoint !== 'string') {
      continue;
    }
    const key = `${ecid}:${ecn}:${meteringPoint}`;
    if (!lookup.has(key)) {
      lookup.set(key, []);
    }
    lookup.get(key).push(mpm);
  }
  return lookup;
}

/**
 * Fetch and normalize metering-point memberships from the backend.
 *
 * @returns {Promise<Array>}
 */
async function fetchMemberships() {
  const response = await axios.get(`${BACKEND_BASEURL}metadata/memberships`);
  const list = Array.isArray(response.data) ? response.data : [];
  return list.map((mpm) => ({
    ...mpm,
    mpmObjectId: mpm._id,
    dateStart: new Date(mpm.dateStart),
    dateEnd: mpm.dateEnd ? new Date(mpm.dateEnd) : mpm.dateEnd,
  }));
}

/**
 * Use injected memberships when non-empty; otherwise fetch from the backend.
 *
 * @param {Array|undefined} memberships
 * @returns {Promise<Array>}
 */
async function resolveMemberships(memberships) {
  if (Array.isArray(memberships) && memberships.length > 0) {
    return memberships;
  }
  return fetchMemberships();
}

function resolveDsoDisplayLabel(ecnDso) {
  try {
    return `${ecnDso} - ${DsoTools.getDsoByEcn(ecnDso).title}`;
  } catch {
    return ecnDso;
  }
}

/**
 * Collect dates between dateStart and dateEnd inclusive (local midnight steps).
 *
 * @param {Date} dateStart
 * @param {Date} dateEnd
 * @returns {Date[]}
 */
function collectDatesInRange(dateStart, dateEnd) {
  const dates = [];
  const cursor = normalizeToLocalMidnight(new Date(dateStart));
  const end = normalizeToLocalMidnight(new Date(dateEnd));
  while (cursor.getTime() <= end.getTime()) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/**
 * Build aggregated summary for the energy-data request preview dialog.
 *
 * @param {Array<{ meteringPointsData: object[], dateStart: Date, dateEnd: Date }>} requests
 * @param {Array<{ ecid: string, ecn: string, meteringPoint: string }>} unresolvedMeterPoints
 * @param {Map<string, object>} communitiesByEcid
 * @returns {EnergyDataRequestSummary}
 */
export function buildEnergyDataRequestSummary(
  requests,
  unresolvedMeterPoints,
  communitiesByEcid
) {
  /** @type {Map<string, { ecid: string, ecn: string, displayLabel: string, meteringPoints: Set<string>, requestCount: number, dates: Date[] }>} */
  const byCommunityMap = new Map();
  /** @type {Map<string, { ecnDso: string, displayLabel: string, meteringPoints: Set<string>, requestCount: number, dates: Date[] }>} */
  const byDsoMap = new Map();
  const allMeteringPoints = new Set();

  for (const req of requests) {
    const mp = req.meteringPointsData[0];
    const community = communitiesByEcid.get(mp.ecid) ?? null;
    const ecnDso = resolveEcnDso(community, mp.meteringPoint);
    const communityName = community?.name ?? mp.ecid;
    const communityLabel = `${mp.ecn} - ${communityName}`;
    const dsoLabel = resolveDsoDisplayLabel(ecnDso);
    const rangeDates = collectDatesInRange(req.dateStart, req.dateEnd);

    allMeteringPoints.add(mp.meteringPoint);

    if (!byCommunityMap.has(mp.ecid)) {
      byCommunityMap.set(mp.ecid, {
        ecid: mp.ecid,
        ecn: mp.ecn,
        displayLabel: communityLabel,
        meteringPoints: new Set(),
        requestCount: 0,
        dates: [],
      });
    }
    const communityGroup = byCommunityMap.get(mp.ecid);
    communityGroup.meteringPoints.add(mp.meteringPoint);
    communityGroup.requestCount += 1;
    communityGroup.dates.push(...rangeDates);

    if (!byDsoMap.has(ecnDso)) {
      byDsoMap.set(ecnDso, {
        ecnDso,
        displayLabel: dsoLabel,
        meteringPoints: new Set(),
        requestCount: 0,
        dates: [],
      });
    }
    const dsoGroup = byDsoMap.get(ecnDso);
    dsoGroup.meteringPoints.add(mp.meteringPoint);
    dsoGroup.requestCount += 1;
    dsoGroup.dates.push(...rangeDates);
  }

  const byCommunity = [...byCommunityMap.values()]
    .map((group) => ({
      ecid: group.ecid,
      ecn: group.ecn,
      displayLabel: group.displayLabel,
      meteringPointCount: group.meteringPoints.size,
      requestCount: group.requestCount,
      dateRanges: formatDateRangesForDisplay(group.dates),
    }))
    .sort((a, b) =>
      String(a.ecn).localeCompare(String(b.ecn), undefined, { numeric: true })
    );

  const byDso = [...byDsoMap.values()]
    .map((group) => ({
      ecnDso: group.ecnDso,
      displayLabel: group.displayLabel,
      meteringPointCount: group.meteringPoints.size,
      requestCount: group.requestCount,
      dateRanges: formatDateRangesForDisplay(group.dates),
    }))
    .sort((a, b) =>
      String(a.ecnDso).localeCompare(String(b.ecnDso), undefined, { numeric: true })
    );

  return {
    totals: {
      requestCount: requests.length,
      meteringPointCount: allMeteringPoints.size,
      communityCount: byCommunityMap.size,
      dsoCount: byDsoMap.size,
      unresolvedMeterPointCount: unresolvedMeterPoints.length,
    },
    byCommunity,
    byDso,
    unresolvedMeterPoints,
  };
}

/**
 * Aggregate issue dates per ecid+meteringPoint across qualifying billruns.
 *
 * @param {Array} billruns
 * @param {Record<string, object|null>} analysisByBillRunKey
 * @param {Record<string, import('@/services/billrunEnergyDataInterpretationService').EnergyDataInterpretation>} interpretationByBillRunKey
 * @param {boolean} billingRelevantOnly
 * @returns {Map<string, AggregatedMeterPoint>}
 */
function aggregateIssueDatesByMeterPoint(
  billruns,
  analysisByBillRunKey,
  interpretationByBillRunKey,
  billingRelevantOnly
) {
  const aggregated = new Map();
  const options = { billingRelevantOnly };

  for (const billrun of billruns) {
    const key = billRunAnalysisKey(billrun.ecid, billrun.runIdentifier);
    const interpretation = interpretationByBillRunKey[key];
    if (!interpretation || !shouldIncludeBillrun(billrun, interpretation)) {
      continue;
    }
    const analysis = analysisByBillRunKey[key] ?? null;
    const filtered = getFilteredIssueResults(analysis, options);

    if (!hasRelevantIssueEntries(filtered)) {
      continue;
    }

    const datesByMp = collectIssueDatesByMeterPoint(filtered);
    for (const [meteringPoint, dates] of datesByMp) {
      const aggKey = `${billrun.ecid}:${meteringPoint}`;
      if (!aggregated.has(aggKey)) {
        aggregated.set(aggKey, {
          ecid: billrun.ecid,
          ecn: billrun.ecn,
          meteringPoint,
          dates: [],
        });
      }
      mergeDatesIntoBucket(aggregated.get(aggKey), dates);
    }
  }

  return aggregated;
}

/**
 * Build requestEnergyData objects from aggregated meter points, applying view filters
 * and resolving memberships.
 *
 * @param {Map<string, AggregatedMeterPoint>} aggregated
 * @param {Map<string, object[]>} membershipLookup
 * @param {Map<string, object>} communitiesByEcid
 * @param {Set<string>} dsoFilter
 * @param {Set<string>} communityFilter
 * @returns {Omit<BuildEnergyDataRequestsResult, 'summary'>}
 */
function buildRequestsFromAggregated(
  aggregated,
  membershipLookup,
  communitiesByEcid,
  dsoFilter,
  communityFilter
) {
  const requests = [];
  const unresolvedMeterPoints = [];

  const sortedBuckets = [...aggregated.values()].sort((a, b) => {
    const ecnCmp = String(a.ecn).localeCompare(String(b.ecn), undefined, { numeric: true });
    if (ecnCmp !== 0) {
      return ecnCmp;
    }
    return String(a.meteringPoint).localeCompare(String(b.meteringPoint));
  });

  for (const bucket of sortedBuckets) {
    const community = communitiesByEcid.get(bucket.ecid) ?? null;
    const ecnDso = resolveEcnDso(community, bucket.meteringPoint);

    if (!passesViewFilters({ ecid: bucket.ecid, ecnDso }, dsoFilter, communityFilter)) {
      continue;
    }

    const dateRanges = datesToDateRanges(bucket.dates);
    if (dateRanges.length === 0) {
      continue;
    }

    const membershipKey = `${bucket.ecid}:${bucket.ecn}:${bucket.meteringPoint}`;
    const membershipCandidates = membershipLookup.get(membershipKey) ?? [];

    if (membershipCandidates.length === 0) {
      unresolvedMeterPoints.push({
        ecid: bucket.ecid,
        ecn: bucket.ecn,
        meteringPoint: bucket.meteringPoint,
      });
      continue;
    }

    let anyRangeBuilt = false;
    for (const { dateStart, dateEnd } of dateRanges) {
      const membership = _resolveMembershipForDateRange(
        membershipCandidates,
        dateStart,
        dateEnd
      );
      if (!membership) {
        continue;
      }
      requests.push({
        meteringPointsData: [membership],
        dateStart,
        dateEnd,
      });
      anyRangeBuilt = true;
    }

    if (!anyRangeBuilt) {
      unresolvedMeterPoints.push({
        ecid: bucket.ecid,
        ecn: bucket.ecn,
        meteringPoint: bucket.meteringPoint,
      });
      continue;
    }
  }

  return { requests, unresolvedMeterPoints };
}

/**
 * Build requestEnergyData payloads for not-billable billrun issue meter points.
 * Companion to ViewBillrunsDetails — accepts store snapshot and view filter params.
 *
 * @param {BuildEnergyDataRequestsInput} input
 * @returns {Promise<BuildEnergyDataRequestsResult>}
 */
export async function buildEnergyDataRequests({
  billruns,
  analysisByBillRunKey,
  interpretationByBillRunKey,
  billingRelevantOnly,
  selectedDsoEcns = [],
  selectedCommunityEcids = [],
  communitiesByEcid,
  memberships,
}) {
  const { dsoFilter, communityFilter } = toFilterSets(selectedDsoEcns, selectedCommunityEcids);

  const aggregated = aggregateIssueDatesByMeterPoint(
    billruns,
    analysisByBillRunKey,
    interpretationByBillRunKey,
    billingRelevantOnly
  );

  const membershipList = await resolveMemberships(memberships);
  const membershipLookup = buildMembershipLookup(membershipList);

  const { requests, unresolvedMeterPoints } = buildRequestsFromAggregated(
    aggregated,
    membershipLookup,
    communitiesByEcid,
    dsoFilter,
    communityFilter
  );

  const summary = buildEnergyDataRequestSummary(
    requests,
    unresolvedMeterPoints,
    communitiesByEcid
  );

  return { requests, unresolvedMeterPoints, summary };
}

/** Build the blueprint request preview directly from period-analysis result rows. */
export async function buildEnergyDataRequestsFromRows({ rows, communitiesByEcid, memberships }) {
  const aggregated = new Map();
  for (const row of rows) {
    const key = `${row.ecid}:${row.meteringPoint}`;
    if (!aggregated.has(key)) {
      aggregated.set(key, {
        ecid: row.ecid,
        ecn: row.ecn,
        meteringPoint: row.meteringPoint,
        dates: [],
      });
    }
    mergeDatesIntoBucket(aggregated.get(key), row.issueDates ?? []);
  }
  const membershipList = await resolveMemberships(memberships);
  const membershipLookup = buildMembershipLookup(membershipList);
  const { requests, unresolvedMeterPoints } = buildRequestsFromAggregated(
    aggregated,
    membershipLookup,
    communitiesByEcid,
    new Set(),
    new Set()
  );
  const summary = buildEnergyDataRequestSummary(requests, unresolvedMeterPoints, communitiesByEcid);
  return { requests, unresolvedMeterPoints, summary };
}

/**
 * @typedef {Object} SubmitEnergyDataRequestsResult
 * @property {number} requestsTotal
 * @property {number} callsGenerated
 * @property {number} callsSucceeded
 * @property {number} callsFailed
 */

/**
 * Run async tasks with a fixed worker pool concurrency.
 *
 * @template T
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<void>} fn
 * @returns {Promise<void>}
 */
async function runWithConcurrency(items, concurrency, fn) {
  if (items.length === 0) {
    return;
  }
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      await fn(items[index], index);
    }
  }

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

/**
 * Submit built requestEnergyData payloads via startEnergyDataRequest with fixed concurrency.
 *
 * @param {Array<{ meteringPointsData: object[], dateStart: Date, dateEnd: Date }>} requests
 * @param {{ onProgress?: (progress: { completed: number, total: number }) => void }} [options]
 * @returns {Promise<SubmitEnergyDataRequestsResult>}
 */
export async function submitEnergyDataRequests(requests, { onProgress } = {}) {
  const total = requests.length;
  if (total === 0) {
    return {
      requestsTotal: 0,
      callsGenerated: 0,
      callsSucceeded: 0,
      callsFailed: 0,
    };
  }

  let completed = 0;
  let callsGenerated = 0;
  let callsSucceeded = 0;
  let callsFailed = 0;

  await runWithConcurrency(requests, SUBMIT_CONCURRENCY, async (request) => {
    const result = await startEnergyDataRequest(request);
    callsGenerated += result.callsGenerated ?? 0;
    callsSucceeded += result.callsSucceeded ?? 0;
    callsFailed += result.callsFailed ?? 0;
    completed += 1;
    onProgress?.({ completed, total });
  });

  return {
    requestsTotal: total,
    callsGenerated,
    callsSucceeded,
    callsFailed,
  };
}
